import { isPending, type MetricValue } from '@fleetcore/shared'

import { cn } from '../../lib/cn'
import {
  arcPath,
  GAUGE_START_ANGLE,
  GAUGE_SWEEP_ANGLE,
  scaleFraction,
  tickAngles,
} from './geometry'
import { usePendingLabel } from './pending-label'

/**
 * A dial, drawn with inline SVG and no charting dependency.
 *
 * Two constraints shaped it. It must read at 88 px on a tablet mounted in a
 * dispatch office, so the number is inside the dial at a size that survives the
 * scale-down and the tick marks carry no labels of their own. And it must be
 * correct in both themes from one markup: every stroke and fill is
 * `currentColor` under a token class, never a literal — a hard-coded #1f2937
 * looks deliberate in light mode and disappears in dark.
 *
 * An unmeasured dial draws its track and NOTHING else — no needle at zero, no
 * scale end-points. A dial resting on 0 is a measurement; this is the absence of
 * one, and the two must not look alike.
 */

const CX = 50
const CY = 48
const RADIUS = 36
const TRACK_WIDTH = 8
const TICK_COUNT = 9

export interface ArcGaugeProps {
  label: string
  metric: MetricValue<number>
  /** Bottom of the scale. Almost always 0 — negative dials exist for temperatures. */
  min?: number
  max: number
  /** Turns a measured value into display text — locale-aware, unit-aware. */
  format: (value: number) => string
  /** Rendered small, under the number. The unit symbol, usually. */
  unit?: string
  /** Fraction of the scale from which the arc reads as a warning, e.g. a redline. */
  redlineFrom?: number
  hint?: string
  /**
   * Replaces the generic pending wording with a resolved sentence.
   *
   * It exists for the one absence a catalog key cannot express: "ne remonte plus
   * depuis le 14 mars 2026" carries a date, and `PendingMetric.reasonKey` is a bare
   * key with nowhere to interpolate one. Ignored when the metric is available.
   */
  pendingText?: string
  size?: 'sm' | 'md'
  className?: string
}

const SIZES: Record<'sm' | 'md', string> = {
  sm: 'h-[88px] w-[88px]',
  md: 'h-28 w-28',
}

export function ArcGauge({
  label,
  metric,
  min = 0,
  max,
  format,
  unit,
  redlineFrom,
  hint,
  pendingText,
  size = 'md',
  className,
}: ArcGaugeProps) {
  const pendingLabel = usePendingLabel()
  const pending = isPending(metric)

  const fraction = pending ? 0 : scaleFraction(metric.value, min, max)
  const valueText = pending ? (pendingText ?? pendingLabel(metric)) : format(metric.value)
  const overRedline = redlineFrom !== undefined && fraction >= redlineFrom

  const track = arcPath(CX, CY, RADIUS, GAUGE_START_ANGLE, GAUGE_START_ANGLE + GAUGE_SWEEP_ANGLE)
  const value = arcPath(
    CX,
    CY,
    RADIUS,
    GAUGE_START_ANGLE,
    GAUGE_START_ANGLE + GAUGE_SWEEP_ANGLE * fraction,
  )

  return (
    <figure className={cn('flex min-w-0 flex-col items-center gap-1', className)}>
      <svg
        role="img"
        aria-label={`${label} : ${valueText}`}
        viewBox="0 0 100 100"
        className={cn('shrink-0', SIZES[size])}
      >
        <path
          d={track}
          fill="none"
          stroke="currentColor"
          strokeWidth={TRACK_WIDTH}
          strokeLinecap="round"
          className="text-surface-muted"
        />

        {/* Ticks orient the eye on a dial with no printed scale. Hidden while the
            value is unknown, so the dial cannot be misread as resting on zero. */}
        {!pending
          ? tickAngles(TICK_COUNT).map((angle) => (
              <line
                key={angle}
                x1={CX}
                y1={CY - RADIUS + TRACK_WIDTH / 2 + 3}
                x2={CX}
                y2={CY - RADIUS - TRACK_WIDTH / 2 - 1}
                transform={`rotate(${angle} ${CX} ${CY})`}
                stroke="currentColor"
                strokeWidth={1}
                className="text-border-strong"
              />
            ))
          : null}

        {!pending ? (
          <path
            d={value}
            fill="none"
            stroke="currentColor"
            strokeWidth={TRACK_WIDTH}
            strokeLinecap="round"
            className={overRedline ? 'text-danger' : 'text-accent'}
          />
        ) : null}

        {!pending ? (
          <>
            <text
              x={CX}
              y={unit ? CY + 4 : CY + 7}
              textAnchor="middle"
              className="fill-current text-[19px] font-semibold tabular-nums text-foreground"
            >
              {format(metric.value)}
            </text>
            {unit ? (
              <text
                x={CX}
                y={CY + 17}
                textAnchor="middle"
                className="fill-current text-[9px] font-medium uppercase tracking-wide text-foreground-subtle"
              >
                {unit}
              </text>
            ) : null}
          </>
        ) : null}
      </svg>

      <figcaption className="min-w-0 text-center">
        <p className="truncate text-xs font-medium text-foreground">{label}</p>
        {pending ? (
          <p className="text-xs leading-snug text-foreground-subtle">{valueText}</p>
        ) : hint ? (
          <p className="truncate text-xs text-foreground-subtle">{hint}</p>
        ) : null}
      </figcaption>
    </figure>
  )
}
